import React, { useEffect, useState, useCallback } from "react";
import api, { apiErr } from "@/lib/api";
import { toast } from "sonner";
import { EMPTY_FEE, feePayload } from "@/components/fees/feeUtils";
import { FeeSummaryCards } from "@/components/fees/FeeSummaryCards";
import { FeeAddForm } from "@/components/fees/FeeAddForm";
import { FeeRow } from "@/components/fees/FeeRow";
import { FeeEditDialog } from "@/components/fees/FeeEditDialog";
import FilePreviewSheet from "@/components/files/FilePreviewSheet";

export default function FeesTab({ projectId }) {
  const [fees, setFees] = useState([]);
  const [summary, setSummary] = useState({ total: 0, paid: 0, outstanding: 0, count: 0 });
  const [project, setProject] = useState({ client: "", builder: "" });
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(EMPTY_FEE);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(null);
  const [preview, setPreview] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/projects/${projectId}/fees`);
      setFees(data.fees || []);
      setSummary(data.summary || { total: 0, paid: 0, outstanding: 0, count: 0 });
      setProject(data.project || { client: "", builder: "" });
    } catch (e) {
      toast.error(apiErr(e, "Could not load fees"));
    } finally { setLoading(false); }
  }, [projectId]);

  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!form.label.trim()) { toast.error("Add a fee label"); return; }
    if (form.amount === "" || isNaN(Number(form.amount)) || Number(form.amount) < 0) { toast.error("Enter a valid amount"); return; }
    setSaving(true);
    try {
      await api.post(`/projects/${projectId}/fees`, feePayload(form));
      setForm(EMPTY_FEE);
      toast.success("Fee added");
      load();
    } catch (e) { toast.error(apiErr(e, "Could not add fee")); }
    finally { setSaving(false); }
  };

  const quickStatus = async (fee, status) => {
    try {
      await api.patch(`/projects/${projectId}/fees/${fee.fee_id}`, { status });
      load();
    } catch (e) { toast.error("Could not update status"); }
  };

  const startEdit = (f) => setEditing({
    ...f, amount: String(f.amount), due_date: f.due_date || "", note: f.note || "",
    invoice_number: f.invoice_number || "", paid_date: f.paid_date || "", gst: !!f.gst,
    paid_by: f.paid_by || "", invoiced_to_type: f.invoiced_to_type || "",
    invoiced_to_name: f.invoiced_to_name || "", payment_method: f.payment_method || "",
    payment_reference: f.payment_reference || "",
    reimbursable: !!f.reimbursable, reimbursed: !!f.reimbursed, reimbursed_date: f.reimbursed_date || "",
    attachments: f.attachments || [],
  });

  const saveEdit = async () => {
    if (!editing.label.trim()) { toast.error("Add a fee label"); return; }
    setSaving(true);
    try {
      await api.patch(`/projects/${projectId}/fees/${editing.fee_id}`,
        { ...feePayload(editing), paid_date: editing.paid_date || null });
      setEditing(null);
      toast.success("Fee updated");
      load();
    } catch (e) { toast.error(apiErr(e, "Could not update fee")); }
    finally { setSaving(false); }
  };

  const remove = async (feeId) => {
    try { await api.delete(`/projects/${projectId}/fees/${feeId}`); toast.success("Fee removed"); load(); }
    catch (e) { toast.error("Could not remove fee"); }
  };

  return (
    <div data-testid="fees-tab">
      <FeeSummaryCards summary={summary} />
      <FeeAddForm form={form} setForm={setForm} onCreate={create} saving={saving} project={project} projectId={projectId} />

      <div className="border border-zinc-200 bg-white">
        <div className="px-4 py-3 border-b border-zinc-200 text-[10px] font-semibold uppercase tracking-[0.15em] text-zinc-500 grid grid-cols-[minmax(0,1fr)_120px_130px_110px_70px] gap-3">
          <span>Fee</span><span>Amount</span><span>Status</span><span>Due</span><span></span>
        </div>
        {loading ? (
          <div className="p-8"><div className="h-6 w-6 border-2 border-zinc-900 border-t-transparent rounded-full animate-spin" /></div>
        ) : fees.length === 0 ? (
          <p className="p-6 text-sm text-zinc-400">No fees recorded yet. Add the first one above.</p>
        ) : fees.map((f) => (
          <FeeRow key={f.fee_id} f={f} project={project} onQuickStatus={quickStatus} onEdit={startEdit} onRemove={remove} onPreview={setPreview} />
        ))}
      </div>

      <FeeEditDialog editing={editing} setEditing={setEditing} onSave={saveEdit} saving={saving} project={project} onPreview={setPreview} />
      <FilePreviewSheet file={preview} onClose={() => setPreview(null)} />
    </div>
  );
}
